import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page } from '../components/Page.js';

// The ledger panel, chain-verified status, AND the reminders panel/next-deadline
// stat are now REAL (served by /api/ledger and /api/reminders). Device/recon
// counts remain sample until CRUD list + reconciliation (M7) land.
type Reminder = {
  id: number; rule_title: string; subject_label: string | null; due_date: string;
  state: 'overdue' | 'due_soon' | 'upcoming' | 'snoozed' | 'satisfied'; days_to_due: number; plain: string;
};
type Counts = { actionable: number; overdue: number; due_soon: number; total: number };

const STATE_DOT: Record<Reminder['state'], string> = {
  overdue: 'bad', due_soon: 'warn', upcoming: 'ok', snoozed: 'warn', satisfied: 'ok',
};
function badgeFor(r: Reminder): { kind: string; text: string } {
  if (r.state === 'overdue') return { kind: 'bad', text: `Overdue ${Math.abs(r.days_to_due)}d` };
  if (r.state === 'due_soon') return { kind: 'warn', text: r.days_to_due === 0 ? 'Due today' : `Due ${r.days_to_due}d` };
  if (r.state === 'snoozed') return { kind: 'mute', text: 'Snoozed' };
  if (r.state === 'satisfied') return { kind: 'ok', text: 'Done' };
  return { kind: 'mute', text: `In ${r.days_to_due}d` };
}

const OP_KIND: Record<string, string> = {
  notify: 'ok', anchor: 'ok', create: 'ok', attach: 'ok',
  disclose: 'mute', genesis: 'mute', update: 'mute', status_change: 'mute',
  reconcile: 'warn', import: 'warn', tombstone: 'warn', crypto_shred: 'warn', key_mgmt: 'warn',
};

type LedgerEntry = { seq: number; op: string; ts: string; summary: string; entity_type: string | null };
type Head = { height: number; seq: number | null; chain_hash: string | null; ts: string | null };

function shortHash(h: string | null): string {
  if (!h) return '—';
  return `${h.slice(0, 8)}…${h.slice(-8)}`;
}
function fmt(ts: string): string {
  return ts.replace('T', ' ').replace(/\.\d+Z$/, 'Z');
}

export function Dashboard() {
  const nav = useNavigate();
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [head, setHead] = useState<Head | null>(null);
  const [verified, setVerified] = useState<boolean | null>(null);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [devices, setDevices] = useState<{ status: string; last_reviewed_at: string | null }[]>([]);
  const [stale, setStale] = useState<number | null>(null);
  const [anchor, setAnchor] = useState<{ status: string; bitcoin_block_height: number | null; submitted_at: string } | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [h, l, rem, dev, rec, anc] = await Promise.all([
          fetch('/api/ledger/head').then((r) => r.json()),
          fetch('/api/ledger?limit=6').then((r) => r.json()),
          fetch('/api/reminders').then((r) => r.json()),
          fetch('/api/devices').then((r) => r.json()),
          fetch('/api/reconcile').then((r) => r.json()),
          fetch('/api/anchors').then((r) => r.json()),
        ]);
        if (!live) return;
        setHead(h.head);
        setVerified(h.verify?.ok ?? null);
        setEntries(l.entries ?? []);
        setReminders(rem.reminders ?? []);
        setCounts(rem.counts ?? null);
        setDevices(dev.devices ?? []);
        setStale(rec.buckets?.counts?.stale_open ?? null);
        setAnchor(anc.latest ?? null);
      } catch {
        if (live) { setVerified(null); setError(true); }
      }
    })();
    return () => { live = false; };
  }, []);

  const actionable = reminders.filter((r) => r.state === 'overdue' || r.state === 'due_soon' || r.state === 'upcoming');
  const topTodos = actionable.slice(0, 4);
  const next = actionable.find((r) => r.state === 'overdue') ?? actionable[0] ?? null;
  const nextLabel = next
    ? (next.days_to_due < 0 ? `${Math.abs(next.days_to_due)}d late` : next.days_to_due === 0 ? 'today' : `${next.days_to_due}d`)
    : '—';
  const gone = ['disposed', 'sold', 'lost', 'stolen', 'returned'];
  const devActive = devices.filter((x) => !gone.includes(x.status)).length;
  const devGone = devices.length - devActive;

  const REVIEW_DUE_DAYS = 182;
  function isReviewDue(last_reviewed_at: string | null): boolean {
    if (!last_reviewed_at) return true;
    return Date.now() - new Date(last_reviewed_at).getTime() > REVIEW_DUE_DAYS * 86400000;
  }
  const devReviewDue = devices.filter((x) => isReviewDue(x.last_reviewed_at)).length;

  return (
    <Page
      eyebrow="Records"
      title="Today"
      actions={
        <>
          <span className="pill pill-ok">
            <span className="dot dot-ok" />
            {verified === null
              ? 'Ledger…'
              : verified
                ? `Chain verified · height ${head?.height ?? 0} · head ${shortHash(head?.chain_hash ?? null)}`
                : 'Chain verification FAILED'}
          </span>
          <button className="btn btn-primary" type="button" onClick={() => nav('/reminders')}>+ Record notification</button>
        </>
      }
    >
      <section className="crown">
        <div>
          <div className="crown-eyebrow">The Crown Guarantee</div>
          <p>
            Every record is hash-chained and anchored into the Bitcoin blockchain. Anyone can verify{' '}
            <b>when</b> a record existed — without seeing its contents and without trusting you or this app.
          </p>
        </div>
        <div className="crown-head">
          <div className="lab">Bitcoin anchor</div>
          <div className="val">
            {!anchor ? shortHash(head?.chain_hash ?? null)
              : anchor.status === 'confirmed' ? `block ${anchor.bitcoin_block_height}` : 'submitted'}
          </div>
          <div className="sub">
            {!anchor ? `head seq ${head?.seq ?? '—'}`
              : anchor.status === 'confirmed' ? 'confirmed in Bitcoin' : `pending · ${fmt(anchor.submitted_at)}`}
          </div>
        </div>
      </section>

      {error && (
        <div className="card card-pad" style={{ marginBottom: 16, borderLeft: '3px solid var(--bad)', color: 'var(--bad)', fontSize: 13 }}>
          Couldn’t load your live data — your session may be locked or you’re offline. The figures below
          may be out of date; <b>“nothing due” here is not confirmation that nothing is due.</b>{' '}
          <a href="#" onClick={(e) => { e.preventDefault(); window.location.reload(); }}>Reload</a>.
        </div>
      )}

      <section className="stats">
        <div className="card card-pad stat">
          <div className="lab">Devices on record</div>
          <div className="num">{devices.length}</div>
          <div className="sub">{devActive} active · {devGone} disposed/gone</div>
          {devReviewDue > 0 && (
            <div className="sub" style={{ marginTop: 4 }}>
              <span
                style={{ color: 'var(--warn, #b45309)', cursor: 'pointer', fontSize: 12 }}
                onClick={() => nav('/devices')}
              >
                {devReviewDue} device{devReviewDue !== 1 ? 's' : ''} due for review
              </span>
            </div>
          )}
        </div>
        <div className="card card-pad stat">
          <div className="lab">Reconciliation flags</div>
          <div className={`num ${stale && stale > 0 ? 'bad' : stale === 0 ? 'ok' : ''}`}>{stale ?? '—'}</div>
          <div className="sub">stale on Police list</div>
        </div>
        <div className="card card-pad stat">
          <div className="lab">Next deadline</div>
          <div className={`num ${next?.state === 'overdue' ? 'bad' : next?.state === 'due_soon' ? 'warn' : next ? 'ok' : ''}`}>{nextLabel}</div>
          <div className="sub">{next ? next.rule_title.toLowerCase() : 'nothing due'}</div>
        </div>
        <div className="card card-pad stat">
          <div className="lab">Ledger height</div>
          <div className="num ok">{head?.height ?? '—'}</div>
          <div className="sub">{verified ? 'chain verified' : 'verifying…'}</div>
        </div>
      </section>

      <section className="cols2">
        <div className="card">
          <div className="panel-head">
            <h3>What I need to do</h3>
            <span className="hint">{counts ? `${counts.overdue} overdue · ${counts.due_soon} due soon` : 'plain-English deadlines'}</span>
          </div>
          <div className="todo">
            {topTodos.length === 0 && (
              <div style={{ padding: 16, color: 'var(--ink3)', fontSize: 13 }}>
                Nothing outstanding right now. Deadlines appear here as you add devices, accounts and travel.
              </div>
            )}
            {topTodos.map((t) => {
              const b = badgeFor(t);
              return (
                <div className="todo-item" key={t.id} style={{ cursor: 'pointer' }} onClick={() => nav('/reminders')}>
                  <span className={`dot dot-${STATE_DOT[t.state]}`} />
                  <div className="todo-main">
                    <div className="todo-title">{t.rule_title}{t.subject_label ? ` — ${t.subject_label}` : ''}</div>
                    <div className="todo-sub">{t.plain}</div>
                  </div>
                  <span className={`badge badge-${b.kind}`}>{b.text}</span>
                </div>
              );
            })}
          </div>
          <div className="panel-foot">
            The only way to clear an item is to record the real notification — which is itself anchored.
          </div>
        </div>

        <div className="card">
          <div className="panel-head">
            <h3>Ledger activity</h3>
            <span className="hint">append-only · every change is one row</span>
          </div>
          <div className="ledger">
            {entries.length === 0 && <div style={{ padding: 16, color: 'var(--ink3)', fontSize: 13 }}>Loading ledger…</div>}
            {entries.map((l) => (
              <div className="ledger-row" key={l.seq}>
                <span className="ledger-seq">{l.seq}</span>
                <span className={`badge badge-${OP_KIND[l.op] ?? 'mute'}`}>{l.op.toUpperCase()}</span>
                <div className="ledger-main">
                  <div className="ledger-text">{l.summary || l.entity_type || l.op}</div>
                  <div className="ledger-time">{fmt(l.ts)}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="panel-foot mono">verifyChain() ✓ runs before every export</div>
        </div>
      </section>
    </Page>
  );
}
